import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LabelList,
} from "recharts";
import { Dimmer } from "../../spinner";
import axios from "axios";
import { Mensaje } from "@/components/mensajes2";
import { formatMoneda } from "@/util/varios";

const formatearMoneda = (valor) => {
  const num = Number(valor || 0);
  if (Number.isNaN(num)) return "0,00";
  return formatMoneda(num.toFixed(2).replace(".", ","), ",", ".", 2);
};

const abreviar = (valor) => {
  const n = Number(valor || 0);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toString();
};

const StatBox = ({ label, value, color = "#1f4e79", icon }) => (
  <div
    className="d-flex flex-column justify-content-center align-items-center px-1 py-1"
    style={{
      background: "#f8f9fb",
      borderRadius: 8,
      flex: "1 1 calc(50% - 4px)",
      minWidth: 0,
    }}
  >
    <span
      className="text-muted text-truncate w-100 text-center"
      style={{ fontSize: "0.68rem", letterSpacing: 0.2 }}
      title={label}
    >
      {icon} {label}
    </span>
    <span
      className="fw-bold text-truncate w-100 text-center"
      style={{ color, fontSize: "0.82rem", marginTop: 1 }}
      title={value}
    >
      {value}
    </span>
  </div>
);

const GraficasActividad = () => {
  const op = require("../../../modulos/datos");
  const token = localStorage.getItem("jwtToken");
  const anioActual = new Date().getFullYear();
  const idefiscal = JSON.parse(localStorage.getItem("idefiscal"));
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [pagina, setPagina] = useState(1);
  const porPagina = 8;

  const [mensaje, setMensaje] = useState({
    mostrar: false,
    titulo: "",
    texto: "",
    icono: "",
  });

  const buscarGraficaActividad = async () => {
    setLoading(true);
    const endpoint = `${op.conexion}/api/gestion_comercial/consultar-grafica-actividad?anio=${anioActual}&efiscal=${idefiscal}`;
    axios
      .get(endpoint, {
        headers: {
          "x-access-token": `${token}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        if (response.status === 200 && response.data.length > 0) {
          setRecords(response.data);
          setPagina(1);
        }
      })
      .catch(() => {
        setMensaje({
          mostrar: true,
          titulo: "Notificación.",
          texto: "Error al Buscar los Registros",
          icono: "error",
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    buscarGraficaActividad();
  }, []);

  // Precalcula data, total y mes pico por actividad
  const recordsProcesados = useMemo(() => {
    return records.map((item) => {
      const meses = item.meses_json || {};
      const dataMeses = Object.keys(meses).map((mesKey) => ({
        mes: mesKey,
        monto: Number(meses[mesKey]) || 0,
      }));
      const total = dataMeses.reduce((acc, m) => acc + m.monto, 0);
      const pico = dataMeses.reduce(
        (max, m) => (m.monto > max.monto ? m : max),
        { mes: "-", monto: 0 },
      );
      return { ...item, dataMeses, total, pico };
    });
  }, [records]);

  const totalPaginas = Math.ceil(recordsProcesados.length / porPagina) || 1;
  const inicio = (pagina - 1) * porPagina;
  const recordsPagina = recordsProcesados.slice(inicio, inicio + porPagina);

  const generarPaginas = () => {
    const paginas = [];
    const maxVisibles = 5;
    let desde = Math.max(1, pagina - Math.floor(maxVisibles / 2));
    let hasta = Math.min(totalPaginas, desde + maxVisibles - 1);
    if (hasta - desde < maxVisibles - 1) {
      desde = Math.max(1, hasta - maxVisibles + 1);
    }
    if (desde > 1) {
      paginas.push(1);
      if (desde > 2) paginas.push("...");
    }
    for (let i = desde; i <= hasta; i++) paginas.push(i);
    if (hasta < totalPaginas) {
      if (hasta < totalPaginas - 1) paginas.push("...");
      paginas.push(totalPaginas);
    }
    return paginas;
  };

  return (
    <div className="col-12 my-auto pt-3">
      {/* Empty state */}
      {recordsPagina.length === 0 && !loading && (
        <div
          className="text-center text-muted py-5"
          style={{ background: "#f8f9fb", borderRadius: 12 }}
        >
          No hay datos de actividad para mostrar 📭
        </div>
      )}

      {/* Grid 2 columnas */}
      <div className="row g-3 px-1">
        {recordsPagina.map((item, index) => {
          const posicionGlobal = inicio + index + 1;
          return (
            <div className="col-12 col-md-6" key={item.actividad ?? index}>
              <div
                className="card border-0 h-100"
                style={{
                  borderRadius: 14,
                  boxShadow: "0 2px 12px rgba(31,78,121,0.08)",
                  overflow: "hidden",
                }}
              >
                {/* Header card */}
                <div
                  className="d-flex justify-content-between align-items-center px-3 py-2 text-light"
                  style={{
                    background:
                      "linear-gradient(90deg, #1f4e79 0%, #2e86c1 100%)",
                    flexWrap: "nowrap",
                    gap: 8,
                    minWidth: 0,
                    height: 42,
                  }}
                >
                  <div
                    className="d-flex align-items-center gap-2"
                    style={{ minWidth: 0, flex: "1 1 0", overflow: "hidden" }}
                  >
                    <span
                      className="badge bg-light text-dark fw-bold"
                      style={{
                        fontSize: "0.7rem",
                        flexShrink: 0,
                        lineHeight: 1,
                      }}
                    >
                      #{posicionGlobal}
                    </span>
                    <span
                      className="fw-semibold text-light text-truncate"
                      style={{
                        fontSize: "0.95rem",
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        lineHeight: 1,
                        display: "block",
                      }}
                      title={item.actividad}
                    >
                      {item.actividad}
                    </span>
                  </div>

                  <span
                    className="badge fw-bold"
                    style={{
                      background: "rgba(255,255,255,0.22)",
                      color: "#fff",
                      fontSize: "0.7rem",
                      padding: "5px 8px",
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                      lineHeight: 1,
                    }}
                  >
                    📈 Pico: {item.pico.mes}
                  </span>
                </div>

                <div
                  className="px-2 pt-3"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)", // 👈 3 columnas iguales
                    gap: 6,
                  }}
                >
                  <StatBox
                    icon="💰"
                    label="Total anual"
                    value={`Bs ${formatearMoneda(item.total)}`}
                  />
                  <StatBox
                    icon="📅"
                    label="Mes pico"
                    value={`${item.pico.mes} (${abreviar(item.pico.monto)})`}
                    color="#2e86c1"
                  />
                  <StatBox
                    icon="🔝"
                    label="Promedio mensual"
                    value={`Bs ${formatearMoneda(
                      item.dataMeses.length
                        ? item.total / item.dataMeses.length
                        : 0,
                    )}`}
                  />
                </div>

                {/* Gráfica */}
                <div
                  style={{
                    width: "100%",
                    height: 200,
                    padding: "8px 10px 10px",
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={item.dataMeses}
                      margin={{ top: 22, right: 10, left: 0, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient
                          id={`grad-act-${index}`}
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor="#3B82F6"
                            stopOpacity={0.95}
                          />
                          <stop
                            offset="100%"
                            stopColor="#1f4e79"
                            stopOpacity={0.85}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e8eb" />
                      <XAxis
                        dataKey="mes"
                        tick={{ fontSize: 9, fill: "#555" }}
                        axisLine={{ stroke: "#ccc" }}
                        interval={0}
                        height={30}
                        tickFormatter={(v) => v.slice(0, 3)}
                      />
                      <YAxis
                        tickFormatter={(v) => abreviar(v)}
                        tick={{ fontSize: 10, fill: "#777" }}
                        width={42}
                        axisLine={{ stroke: "#ccc" }}
                      />
                      <Tooltip
                        formatter={(v) => [`Bs ${formatearMoneda(v)}`, "Monto"]}
                        labelFormatter={(l) => `Mes: ${l}`}
                        contentStyle={{
                          borderRadius: 8,
                          border: "1px solid #ddd",
                          fontSize: 12,
                        }}
                      />
                      <Bar
                        dataKey="monto"
                        fill={`url(#grad-act-${index})`}
                        radius={[6, 6, 0, 0]}
                        maxBarSize={40}
                      >
                        <LabelList
                          dataKey="monto"
                          position="top"
                          formatter={(v) => abreviar(v)}
                          style={{
                            fontSize: 9,
                            fill: "#333",
                            fontWeight: 600,
                          }}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Paginación */}
      {totalPaginas > 1 && (
        <div className="d-flex justify-content-center align-items-center gap-1 my-4 flex-wrap">
          <button
            className="btn btn-sm btn-outline-secondary"
            disabled={pagina === 1}
            onClick={() => setPagina(1)}
            title="Primera"
          >
            ««
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            disabled={pagina === 1}
            onClick={() => setPagina((p) => p - 1)}
            title="Anterior"
          >
            «
          </button>

          {generarPaginas().map((p, i) =>
            p === "..." ? (
              <span key={`e-${i}`} className="px-2 text-muted">
                …
              </span>
            ) : (
              <button
                key={p}
                className={`btn btn-sm ${
                  p === pagina ? "btn-primary" : "btn-outline-primary"
                }`}
                onClick={() => setPagina(p)}
                style={{ minWidth: 38 }}
              >
                {p}
              </button>
            ),
          )}

          <button
            className="btn btn-sm btn-outline-secondary"
            disabled={pagina === totalPaginas}
            onClick={() => setPagina((p) => p + 1)}
            title="Siguiente"
          >
            »
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            disabled={pagina === totalPaginas}
            onClick={() => setPagina(totalPaginas)}
            title="Última"
          >
            »»
          </button>

          <span className="ms-3 text-muted small">
            Página {pagina} de {totalPaginas} · {recordsProcesados.length}{" "}
            actividades
          </span>
        </div>
      )}

      <Mensaje
        mensaje={mensaje}
        onHide={() =>
          setMensaje({ mostrar: false, titulo: "", texto: "", icono: "" })
        }
      />
      <Dimmer active={loading} inverted />
    </div>
  );
};

export default GraficasActividad;
